// #ux-redesign-start: LoginPage rediseñada (Paso 5 — Patients)
// Reemplaza el form Reactstrap/InputGroup/CardLogin del template legacy por
// AuthLayout + AuthCard + primitivas del DS (Input, PasswordField, Checkbox,
// Button). Validación con react-hook-form v5 + yup.
//
// Paridad con Web-Doctors:
//   - PasskeyButton (WebAuthn 100% funcional) bajo el CTA, con separator "oder".
//   - LegalLinksBar pegada al pie de __main; el contenido abre en el panel
//     izquierdo (desktop) o en Modal (tablet/mobile), reemplazando el
//     LegalContent legacy que iba al pie.
//
// Preserva la lógica de Patients tal cual la legacy:
//   - `:data?` param flow (base64 credentials → auto-login).
//   - localStorage `ts-temp-secret` (consumido por otros flujos).
//   - localStorage `pending_initial_choice` / `tsp-url` (redirect post-login).
//   - tokenName desde configs/app.json.
//   - Bloque debug-info en envs no-demo (como <details> colapsable).
//   - Sección "registración / We can help you" debajo del card (link extra
//     propio de Patients, no presente en Web-Doctors).
// ─────────────────────────────────────────────────────────────────────────────

import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { translate } from 'react-translate';

import { useForm } from 'react-hook-form';
import * as yup from 'yup';

const tokenName = require('configs/app.json').tokenName;
import storageDispatcher from 'helpers/storageDispatcher';

import useInitialUrl from 'hooks/useInitialUrl';
import useResponsive from 'hooks/useResponsive';
import { actionLogin } from 'store/actions/usersActions';

import AuthLayout from 'components/Auth/AuthLayout';
import AuthCard from 'components/Auth/AuthCard';
import PasskeyButton from 'components/Auth/PasskeyButton';
import LegalLinksBar from 'components/Auth/LegalLinksBar';
import { buildLegalSections } from 'components/Auth/legalSections';
import { Button, Input, Checkbox, PasswordField, Modal } from 'components/ds';

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const LoginPage = (props) => {
  const { t } = props;
  const { language } = useSelector((state) => state.userReducer);
  const user = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();
  const urlDefault = useInitialUrl();
  const { isDesktop } = useResponsive();
  const isMounted = useRef(false);

  const { data } = useParams();
  const credentials = data
    ? decodeURIComponent(escape(atob(data))).split(':')
    : null;

  const [submitting, setSubmitting] = useState(false);
  const [openDebug, setOpenDebug] = useState(false);
  const [activeLegalId, setActiveLegalId] = useState(null);

  const showDebug =
    process.env.REACT_APP_ENV_NAME !== 'demo' &&
    process.env.REACT_APP_ENV_NAME !== 'preprod';

  const legalSections = useMemo(
    () => buildLegalSections(language, {
      datenschutz: t('legal-datenschutz'),
      meineDaten:  t('legal-meine-daten'),
      agb:         t('legal-agb'),
      impressum:   t('legal-impressum'),
    }),
    // t es estable por idioma; recreamos sólo cuando cambia el lang
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );
  const activeLegal = legalSections.find((s) => s.id === activeLegalId) || null;

  const loginSchema = yup.object().shape({
    username: yup
      .string()
      .min(5, t('username') + '...')
      .required(t('username') + '...'),
    password: yup
      .string()
      .min(3, t('password') + '...')
      .required(t('password') + '...'),
  });

  const { register, handleSubmit, errors, setValue, getValues } = useForm({
    validationSchema: loginSchema,
    mode: 'onChange',
    defaultValues: {
      username: credentials ? credentials[0] : '',
      password: credentials ? credentials[1] : '',
      remember: false,
    },
  });

  // Pos-passkey: el login real ya quedó hecho desde PasskeyButton via
  // dispatch(actionPasskeyLogin). Acá sólo prellenamos el username para que se
  // vea consistente si el redirect no es inmediato.
  const handlePasskeyVerified = ({ email }) => {
    if (email) setValue('username', email);
  };

  const onSubmit = async (form) => {
    setSubmitting(true);
    storageDispatcher.setLocalStorageEvent('pending_initial_choice', true);
    await dispatch(
      actionLogin({
        username: form.username.trim().toLowerCase(),
        password: form.password.trim(),
      })
    );
    setTimeout(() => setSubmitting(false), 2000);
  };

  // Auto-login cuando llega `:data?` con credenciales base64
  useEffect(() => {
    if (data) {
      storageDispatcher.removeLocalStorageEvent('pending_initial_choice');
      localStorage.removeItem('tsp-url');
      localStorage.setItem('ts-temp-secret', credentials[1]);
      dispatch(
        actionLogin({
          username: credentials[0],
          password: credentials[1],
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Redirect post-auth (mantiene la lógica original de Patients)
  const isAuth = () => {
    if (user.isAuth) {
      const storedUrl = localStorage.getItem('tsp-url');

      if (storedUrl && (storedUrl.includes('lab-studies') || storedUrl.includes('inbox'))) {
        storageDispatcher.removeLocalStorageEvent('pending_initial_choice');
      }

      if (!user.on_delete && localStorage.getItem('pending_initial_choice')) {
        props.history.push('/admin/initial');
        localStorage.removeItem('tsp-url');
      } else if (localStorage.getItem(tokenName)) {
        if (!['/admin/initial', '/admin/dashboard'].includes(urlDefault)) {
          storageDispatcher.removeLocalStorageEvent('pending_initial_choice');
        }
        props.history.push(
          user.on_delete
            ? '/admin/delete-confirmation'
            : !user.is_ready
            ? '/admin/profile'
            : user.tokenInfo
            ? '/admin/lab-studies'
            : urlDefault
        );
        localStorage.removeItem('tsp-url');
      }
    }
  };

  useEffect(() => {
    document.body.classList.add('login-page');
    isAuth();
    return () => {
      document.body.classList.remove('login-page');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAuth]);

  useEffect(() => {
    isMounted.current = true;
    if (isMounted.current && credentials) {
      setValue('username', credentials[0]);
      setValue('password', credentials[1]);
    }
    return () => (isMounted.current = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const footerLinks = (
    <Link to="/auth/forgot-password" name="link_pass_recovery">
      {t('need-help')}
    </Link>
  );

  // En desktop el contenido del legal section reemplaza la ilustración del
  // panel. En tablet/mobile no hay panel — usamos Modal.
  const panelContent = isDesktop && activeLegal ? (
    <div className="ds-auth__panel-legal">
      <header className="ds-auth__panel-legal-head">
        <h2>{activeLegal.label}</h2>
        <button
          type="button"
          className="ds-modal__close"
          onClick={() => setActiveLegalId(null)}
          aria-label={t('legal-close')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>
      <div className="ds-auth__panel-legal-body">
        {activeLegal.content}
      </div>
    </div>
  ) : null;

  const legalBar = (
    <LegalLinksBar
      sections={legalSections}
      activeId={activeLegalId}
      onChange={setActiveLegalId}
    />
  );

  return (
    <Fragment>
      <AuthLayout
        panelTitle={t('panel-title')}
        panelSubtitle={t('panel-subtitle')}
        panelContent={panelContent}
        legalBar={legalBar}
      >
        <AuthCard title={t('login')} description={t('only-registered-patiens')} footerLinks={footerLinks}>
          <form className="ds-auth__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              name="username"
              type="text"
              autoComplete="username"
              label={t('username')}
              iconLeft={<MailIcon />}
              innerRef={register}
              error={errors.username?.message}
            />
            <PasswordField
              name="password"
              autoComplete="current-password"
              label={t('password')}
              iconLeft={<LockIcon />}
              innerRef={register}
              error={errors.password?.message}
            />
            <Checkbox
              name="remember"
              label={t('keepme')}
              innerRef={register}
            />
            <div className="ds-auth__card-actions">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
                name="button_login"
              >
                {t('login')}
              </Button>
              <div className="ds-auth__separator" aria-hidden="true">
                <span>{t('or')}</span>
              </div>
              <PasskeyButton
                label={t('passkey-login')}
                registerLabel={t('passkey-register')}
                missingEmailMessage={t('passkey-missing-email')}
                successMessage={t('passkey-success')}
                registeredMessage={t('passkey-registered')}
                cancelledMessage={t('passkey-cancelled')}
                errorMessage={t('passkey-error')}
                getEmail={() => getValues('username')}
                onVerified={handlePasskeyVerified}
              />
            </div>
          </form>
        </AuthCard>

        <div className="login-BOSSection">
          <div className="mb-3">
            <div>{t('are-you-not-registered')}</div>
            <div>
              <a
                href="#"
                name="link_registration"
                className="link"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('http://termin-service.org');
                }}
              >
                {t('registration')}
              </a>
            </div>
          </div>
          <div>
            <div>{t('need-help-login')}</div>
            <div className="mb-2">
              <Link to="/auth/login-help" className="link" name="link_help">
                {t('need-help-login-button')}
              </Link>
            </div>
          </div>
        </div>

        {showDebug && (
          <details
            className="ds-auth__debug"
            open={openDebug}
            onToggle={(e) => setOpenDebug(e.target.open)}
          >
            <summary>Debug</summary>
            <code>{process.env.REACT_APP_HOST}</code>
          </details>
        )}
      </AuthLayout>

      {/* Tablet/Mobile: legal sections como Modal (panel oculto < 992px) */}
      {!isDesktop && activeLegal && (
        <Modal
          isOpen={!!activeLegal}
          onClose={() => setActiveLegalId(null)}
          title={activeLegal.label}
          size="lg"
        >
          <div className="ds-auth__legal-modal-body">{activeLegal.content}</div>
        </Modal>
      )}
    </Fragment>
  );
};

export default translate('login-page')(LoginPage);
// #ux-redesign-end
